import { createAsyncThunk, createEntityAdapter, createSlice, createSelector } from '@reduxjs/toolkit';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import { apiGet, apiPut, apiPost, apiDel } from 'app/shared-components/util/restAPI';
import { upsertEvent } from './calendarEventsSlice';

/**
 * Creates a synthetic calendar event from a ClientSession
 * Used to update calendar view without expensive API refresh
 */
function createSyntheticCalendarEvent(session) {
  if (!session.managedCalendarEventId || !session.eventInfo) return null;
  
  const startDate = new Date(session.eventInfo.start);
  const endDate = new Date(session.eventInfo.end);
  
  return {
    // Use managedCalendarEventId as ID - matches what Cronofy returns as event_id
    id: session.managedCalendarEventId,
    cronofyId: null, // Don't have Cronofy event_uid yet
    eventId: session.managedCalendarEventId,
    title: session.eventInfo.summary || 'Session',
    description: session.eventInfo.description || '',
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    startTime: startDate.getTime(),
    location: session.eventInfo.location || '',
    calendarId: session.eventInfo.calendarId,
    attendees: [],
    isManaged: true,
    tags: [`ClientService:${session.id}`],
    isSession: true,
    originalEvent: {
      event_id: session.managedCalendarEventId,
      summary: session.eventInfo.summary,
      start: session.eventInfo.start,
      end: session.eventInfo.end
    }
  };
}

const clientSessionAdapter = createEntityAdapter({
  sortComparer: (a, b) => {
    const aStart = a.eventInfo?.start || '';
    const bStart = b.eventInfo?.start || '';
    return aStart.localeCompare(bStart);
  }
});

export const fetchClientSessions = createAsyncThunk(
  'clientSessions/fetchClientSessions',
  async ({ ownerKey, from, to }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientsession/byowner/${ownerKey}`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: from && to ? { rangeCondition: 'BETWEEN', rangeValue1: from, rangeValue2: to } : {}
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchClientSessionsByClient = createAsyncThunk(
  'clientSessions/fetchClientSessionsByClient',
  async ({ clientId, from, to }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientsession/byclient/${clientId}`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: from && to ? { rangeCondition: 'BETWEEN', rangeValue1: from, rangeValue2: to } : {}
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchSessionById = createAsyncThunk(
  'clientSessions/fetchSessionById',
  async (sessionId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientsession/${sessionId}`;
      const options = { headers: {}, response: false };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchSessionByCalendarEvent = createAsyncThunk(
  'clientSessions/fetchSessionByCalendarEvent',
  async (eventId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/clientsession/bycalendarevent';
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: { eventId }
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createSession = createAsyncThunk(
  'clientSessions/createSession',
  async ({ ownerKey, channelId, sessionData }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/clientsession/';
      const body = { ...sessionData };
      if (channelId) {
        body.channelId = channelId;
      }
      const options = { headers: {}, body, response: false };
      const result = await apiPut(apiName, path, options);
      
      // Create synthetic calendar event to update calendar view immediately
      const syntheticEvent = createSyntheticCalendarEvent(result);
      if (syntheticEvent) {
        dispatch(upsertEvent(syntheticEvent));
      }
      
      return result;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createSeries = createAsyncThunk(
  'clientSessions/createSeries',
  async ({ ownerKey, channelId, seriesData }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/clientsession/createseries';
      const body = { 
        clientId: seriesData.clientId,
        clientServiceId: seriesData.clientServiceId,
        eventInfo: seriesData.eventInfo,
        fee: seriesData.fee,
        status: seriesData.status,
        recurrence: seriesData.recurrence,
        windowMonths: seriesData.windowMonths,
        seriesEndDate: seriesData.seriesEndDate,
        template: channelId ? { channelId } : undefined
      };
      const options = { headers: {}, body, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateSession = createAsyncThunk(
  'clientSessions/updateSession',
  async ({ sessionId, updates }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientsession/${sessionId}`;
      const body = { ...updates };
      const options = { headers: {}, body, response: false };
      const result = await apiPut(apiName, path, options);
      
      // Update synthetic calendar event
      const syntheticEvent = createSyntheticCalendarEvent(result);
      if (syntheticEvent) {
        dispatch(upsertEvent(syntheticEvent));
      }
      
      return result;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateSeries = createAsyncThunk(
  'clientSessions/updateSeries',
  async ({ seriesId, updates, scope, fromOccurrenceNumber }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/clientsession/updateseries';
      const { clientId, ...allowedUpdates } = updates;
      const body = {
        seriesId,
        ...allowedUpdates,
        scope,
        fromOccurrenceNumber
      };
      const options = { headers: {}, body, response: false };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const deleteSession = createAsyncThunk(
  'clientSessions/deleteSession',
  async (sessionId, { dispatch, getState }) => {
    dispatch(startLoading());
    try {
      // Get session before deleting to get managedCalendarEventId
      const session = getState().clientSessions.entities[sessionId];
      const eventId = session?.managedCalendarEventId;
      
      const apiName = 'CoreAPI3';
      const path = `/x3/clientsession/${sessionId}`;
      const options = { headers: {}, response: false };
      await apiDel(apiName, path, options);
      
      // Remove from calendar
      if (eventId) {
        const { removeEvent } = await import('./calendarEventsSlice');
        dispatch(removeEvent(eventId));
      }
      
      return sessionId;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const deleteSeries = createAsyncThunk(
  'clientSessions/deleteSeries',
  async (seriesId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/clientsession/deleteseries';
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: { seriesId }
      };
      await apiDel(apiName, path, options);
      return seriesId;
    } finally {
      dispatch(stopLoading());
    }
  }
);

const clientSessionsSlice = createSlice({
  name: 'clientSessions',
  initialState: clientSessionAdapter.getInitialState({
    loading: false,
    byOwnerKey: {},
    byClient: {},
    bySeries: {}
  }),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchClientSessions.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        state.byOwnerKey[ownerKey] = action.payload.map(s => s.id);
        clientSessionAdapter.upsertMany(state, action.payload);
      })
      .addCase(fetchClientSessionsByClient.fulfilled, (state, action) => {
        const clientId = action.meta.arg.clientId;
        state.byClient[clientId] = action.payload.map(s => s.id);
        clientSessionAdapter.upsertMany(state, action.payload);
      })
      .addCase(fetchSessionById.fulfilled, (state, action) => {
        if (action.payload) {
          clientSessionAdapter.upsertOne(state, action.payload);
        }
      })
      .addCase(fetchSessionByCalendarEvent.fulfilled, (state, action) => {
        if (action.payload) {
          clientSessionAdapter.upsertOne(state, action.payload);
        }
      })
      .addCase(createSession.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        const clientId = action.payload.clientId;
        clientSessionAdapter.addOne(state, action.payload);
        
        if (!state.byOwnerKey[ownerKey]) state.byOwnerKey[ownerKey] = [];
        if (!state.byOwnerKey[ownerKey].includes(action.payload.id)) {
          state.byOwnerKey[ownerKey].push(action.payload.id);
        }
        
        if (!state.byClient[clientId]) state.byClient[clientId] = [];
        if (!state.byClient[clientId].includes(action.payload.id)) {
          state.byClient[clientId].push(action.payload.id);
        }
      })
      .addCase(createSeries.fulfilled, (state, action) => {
        // Series creation returns first occurrence, need to refetch to get all
        const ownerKey = action.meta.arg.ownerKey;
        const clientId = action.payload.clientId;
        clientSessionAdapter.addOne(state, action.payload);
        
        if (!state.byOwnerKey[ownerKey]) state.byOwnerKey[ownerKey] = [];
        if (!state.byOwnerKey[ownerKey].includes(action.payload.id)) {
          state.byOwnerKey[ownerKey].push(action.payload.id);
        }
        
        if (!state.byClient[clientId]) state.byClient[clientId] = [];
        if (!state.byClient[clientId].includes(action.payload.id)) {
          state.byClient[clientId].push(action.payload.id);
        }
      })
      .addCase(updateSession.fulfilled, (state, action) => {
        clientSessionAdapter.upsertOne(state, action.payload);
      })
      .addCase(updateSeries.fulfilled, (state, action) => {
        // Series update may affect multiple sessions, trigger refetch
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        const sessionId = action.payload;
        clientSessionAdapter.removeOne(state, sessionId);
        
        Object.keys(state.byOwnerKey).forEach(key => {
          state.byOwnerKey[key] = state.byOwnerKey[key].filter(id => id !== sessionId);
        });
        Object.keys(state.byClient).forEach(key => {
          state.byClient[key] = state.byClient[key].filter(id => id !== sessionId);
        });
      })
      .addCase(deleteSeries.fulfilled, (state, action) => {
        // Series deletion removes multiple sessions, trigger refetch
      });
  }
});

const { selectById, selectAll } = clientSessionAdapter.getSelectors();

export const selectSessionsByOwnerKey = (ownerKey) => createSelector(
  [state => state.clientSessions.byOwnerKey, state => state.clientSessions],
  (byOwnerKey, clientSessionsState) => {
    const sessionIds = byOwnerKey[ownerKey] || [];
    return sessionIds.map(id => selectById(clientSessionsState, id)).filter(Boolean);
  }
);

export const selectSessionsByClient = (clientId) => createSelector(
  [state => state.clientSessions.byClient, state => state.clientSessions],
  (byClient, clientSessionsState) => {
    const sessionIds = byClient[clientId] || [];
    return sessionIds.map(id => selectById(clientSessionsState, id)).filter(Boolean);
  }
);

export const selectSessionById = (id) => state => selectById(state.clientSessions, id);

export const selectSessionsInRange = (from, to, ownerKey) => createSelector(
  [selectSessionsByOwnerKey(ownerKey)],
  (sessions) => sessions.filter(session => {
    const start = session.eventInfo?.start;
    if (!start) return false;
    return start >= from && start <= to;
  })
);

export const selectSessionsLoading = state => state.clientSessions.loading;

export default clientSessionsSlice.reducer;
